import React, { useRef, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { signaturesAPI } from '../../lib/api';
import { PenLine, Type, Eraser, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export const SignatureDialog = ({ open, onOpenChange, doc, defaultName = '', onSigned }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);
  const [mode, setMode] = useState('draw');
  const [name, setName] = useState(defaultName);
  const [typed, setTyped] = useState(defaultName);
  const [place, setPlace] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setName(defaultName); setTyped(defaultName); setPlace(''); setMode('draw'); hasDrawn.current = false; } }, [open, defaultName]);

  const clearCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d'); ctx.clearRect(0, 0, c.width, c.height);
    hasDrawn.current = false;
  };

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = '#0a2870'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.stroke(); hasDrawn.current = true; };
  const end = () => { drawing.current = false; };

  const buildTypedPng = () => {
    const c = document.createElement('canvas'); c.width = 500; c.height = 160;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#0a2870'; ctx.font = 'italic 60px "Segoe Script", "Brush Script MT", cursive';
    ctx.textBaseline = 'middle'; ctx.fillText(typed || name, 20, 90);
    return c.toDataURL('image/png');
  };

  const submit = async () => {
    if (!name.trim()) { toast.error('Bitte Namen des Unterzeichners angeben'); return; }
    let sig;
    if (mode === 'draw') {
      if (!hasDrawn.current) { toast.error('Bitte unterschreiben'); return; }
      sig = canvasRef.current.toDataURL('image/png');
    } else {
      if (!typed.trim()) { toast.error('Bitte Namen tippen'); return; }
      sig = buildTypedPng();
    }
    setSaving(true);
    try {
      const { data } = await signaturesAPI.signDocument(doc.id, {
        signature_image_base64: sig, signer_name: name.trim(), place: place.trim() || null,
      });
      toast.success('Dokument signiert & in der Akte abgelegt');
      onOpenChange(false);
      onSigned?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Signieren fehlgeschlagen');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="signature-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Dokument signieren</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground truncate">{doc?.title}</p>

          <div className="flex gap-2">
            <Button type="button" size="sm" variant={mode === 'draw' ? 'default' : 'outline'} onClick={() => setMode('draw')} data-testid="sig-mode-draw"><PenLine className="h-4 w-4 mr-1" /> Zeichnen</Button>
            <Button type="button" size="sm" variant={mode === 'type' ? 'default' : 'outline'} onClick={() => setMode('type')} data-testid="sig-mode-type"><Type className="h-4 w-4 mr-1" /> Tippen</Button>
          </div>

          {mode === 'draw' ? (
            <div className="space-y-2">
              <div className="border rounded-lg bg-muted/20">
                <canvas ref={canvasRef} width={500} height={160} className="w-full touch-none rounded-lg" style={{ height: 160 }}
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="signature-canvas" />
              </div>
              <Button type="button" size="sm" variant="ghost" onClick={clearCanvas}><Eraser className="h-4 w-4 mr-1" /> Löschen</Button>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Unterschrift (getippt)</Label>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Name tippen" data-testid="sig-typed-input" />
              <div className="border rounded-lg p-3 text-center bg-muted/20" style={{ fontFamily: '"Segoe Script","Brush Script MT",cursive', fontStyle: 'italic', fontSize: 32, color: '#0a2870' }}>
                {typed || 'Vorschau'}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Unterzeichner *</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="sig-name-input" /></div>
            <div className="space-y-1.5"><Label>Ort (optional)</Label><Input value={place} onChange={(e) => setPlace(e.target.value)} data-testid="sig-place-input" /></div>
          </div>

          <p className="text-[11px] text-muted-foreground">
            Mit dem Signieren wird eine einfache elektronische Signatur (EES) samt Zeitstempel und Dokument-Hash erzeugt und als neue, signierte Kopie in der Akte gespeichert.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={saving} data-testid="sig-submit-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Rechtsverbindlich signieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
